import type { Image, Paragraph, PhrasingContent, Root, RootContent } from 'mdast';
import { $remark } from '@milkdown/kit/utils';
import { visit } from 'unist-util-visit';

import { parseSizeFromTitle } from './parse';

export const IMAGE_BLOCK_MDAST_TYPE = 'image-block';

type MdastImageBlock = {
  type: typeof IMAGE_BLOCK_MDAST_TYPE;
  url?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
};

type MdastParent = {
  children: RootContent[] | PhrasingContent[];
};

function isIgnorablePhrasing(node: PhrasingContent): boolean {
  if (node.type === 'break') return true;
  if (node.type === 'text' && /^\s*$/.test(node.value)) return true;

  return false;
}

/** Structural only — dimensions come from {@link visitPlatformImageBlockDimensions}. */
function toImageBlock(image: Image): MdastImageBlock {
  return {
    type: IMAGE_BLOCK_MDAST_TYPE,
    url: image.url,
    alt: image.alt ?? '',
    title: image.title ?? undefined,
  };
}

/**
 * Lift trailing (or sole) paragraph images to `image-block` after stock remark-image-block.
 * Handles Platform markdown where images share a paragraph with preceding text/breaks.
 */
export function liftTrailingImagesToImageBlocks(tree: Root): void {
  visit(tree, 'paragraph', (node, index, parent) => {
    if (index === null || index === undefined || !parent || !('children' in parent)) return;
    const paragraph = node as Paragraph;
    const children = paragraph.children ?? [];
    if (children.length === 0) return;

    let imageIndex = -1;
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i]!;
      if (child.type === 'image') {
        imageIndex = i;
        break;
      }
      if (!isIgnorablePhrasing(child)) return;
    }
    if (imageIndex < 0) return;

    const image = children[imageIndex] as Image;
    const block = toImageBlock(image);
    const parentNode = parent as MdastParent;

    // Sole image (optional whitespace/breaks): replace whole paragraph.
    const onlyImage = children.every((child, i) => i === imageIndex || isIgnorablePhrasing(child));
    if (onlyImage) {
      (parentNode.children as RootContent[])[index] = block as unknown as RootContent;

      return;
    }

    // Trailing image after real content: keep text paragraph, insert image-block after.
    const before = children.slice(0, imageIndex).filter((child) => !isIgnorablePhrasing(child) || child.type === 'text');
    // Trim trailing whitespace-only text nodes before the image.
    while (before.length > 0) {
      const last = before[before.length - 1]!;
      if (last.type === 'text' && /^\s*$/.test(last.value)) {
        before.pop();
        continue;
      }
      if (last.type === 'break') {
        before.pop();
        continue;
      }
      break;
    }
    if (before.length === 0) {
      (parentNode.children as RootContent[])[index] = block as unknown as RootContent;

      return;
    }

    paragraph.children = before;
    (parentNode.children as RootContent[]).splice(index + 1, 0, block as unknown as RootContent);
  });
}

/** Extract =WxH from image-block mdast title after remark-image-block has lifted images. */
export function visitPlatformImageBlockDimensions(tree: Root): void {
  visit(
    tree,
    (node) => node.type === IMAGE_BLOCK_MDAST_TYPE,
    (node) => {
      const block = node as unknown as MdastImageBlock;
      const { width, height, cleanTitle } = parseSizeFromTitle(block.title);
      if (width !== undefined) block.width = width;
      if (height !== undefined) block.height = height;
      block.title = cleanTitle;
    },
  );
}

export const remarkPlatformImageBlockDimensions = $remark('platform-image-block-dimensions', () => {
  return () => (tree: Root) => {
    liftTrailingImagesToImageBlocks(tree);
    visitPlatformImageBlockDimensions(tree);
  };
});
